import api from "./api"; // Import the new axios instance
import { products } from "../config/products";

const packageJson = require("../../package.json");

// Normalize order endpoints so getOrders and getAllProductOrder share the exact same handler and cache
const normalizeUrl = (url) => {
    if (!url) return url;
    if (url === '/getAllProductOrder' || url === 'getAllProductOrder') return '/getOrders';
    return url;
};

// In-flight GET request deduplication + response cache to prevent redundant hits.
// The cache holds the resolved response for DEDUP_COOLDOWN_MS after completion so
// rapid re-mounts (React StrictMode), event cascades, and multiple components all
// share the same response without firing duplicate network requests.
const pendingGetRequests = new Map();
const DEDUP_COOLDOWN_MS = 5000; // 5-second cooldown after response

export const invokeGetApi = async (url, params) => {
    const normUrl = normalizeUrl(url);
    const cacheKey = `${normUrl}?${JSON.stringify(params || {})}`;

    if (pendingGetRequests.has(cacheKey)) {
        const cached = pendingGetRequests.get(cacheKey);
        // Return the in-flight promise or the cached resolved response
        return cached.promise || cached.response;
    }

    const entry = {};
    const requestPromise = (async () => {
        try {
            const resp = await api.get(normUrl, { params: params });
            // Cache the resolved response for the cooldown period
            entry.response = resp;
            entry.promise = null;
            setTimeout(() => {
                pendingGetRequests.delete(cacheKey);
            }, DEDUP_COOLDOWN_MS);
            return resp;
        } catch (error) {
            const errResp = error.response || { status: 500, data: { responseMessage: "Network Error" } };
            // On error, clear immediately so next attempt can retry
            pendingGetRequests.delete(cacheKey);
            return errResp;
        }
    })();

    entry.promise = requestPromise;
    pendingGetRequests.set(cacheKey, entry);
    return requestPromise;
};

export const invokeApi = async (url, params, cookies) => {
    const normUrl = normalizeUrl(url);
    // Route read-only order requests through invokeGetApi for deduplication
    if (normUrl === '/getOrders') {
        return invokeGetApi(normUrl, params);
    }
    try {
        return await api.post(normUrl, params);
    } catch (error) {
        return error.response || { status: 500, data: { responseMessage: "Network Error" } };
    }
};

export const invokeFormDataApi = async (url, formData, cookies) => {
    try {
        return await api.post(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            }
        });
    } catch (error) {
        return error.response || { status: 500, data: { responseMessage: "Network Error" } };
    }
};

// Use this for UPDATE (edit) calls that include file uploads — backend expects PUT
export const invokeFormDataPutApi = async (url, formData) => {
    try {
        return await api.put(url, formData, {
            headers: {
                "Content-Type": "multipart/form-data",
            }
        });
    } catch (error) {
        return error.response || { status: 500, data: { responseMessage: "Network Error" } };
    }
};

export const invokeDeleteApi = async (url, params) => {
    try {
        return await api.delete(url, { data: params });
    } catch (error) {
        return error.response || { status: 500, data: { responseMessage: "Network Error" } };
    }
};

export const apiList = {
  // Authentication & Users
  signup: "/signup",
  login: "/login",
  userLogin: "/customerLogin",
  customerLogin: "/customerLogin",
  customerRegister: "/signup",
  getCustomers: "/getCustomers",
  addUser: "/addUser",
  getUsers: "/getUsers",
  getUser: "/getUser",
  updateUser: "/updateUser",
  changePassword: "/changePassword",
  deleteUser: "/deleteUser",
  updateUserRoles: "/updateUserRoles",

  // Products
  featured: "/featured",
  allProducts: "/all",
  getAllProducts: "/getAllProducts",
  addProduct: "/addProduct",
  updateProduct: "/updateProduct", // POST /updateProduct/{id}
  deleteProduct: "/deleteProduct", // POST /deleteProduct/{id}
  updateProductGallery: "/updateProductGallery",
  getAllCategory: "/getAllCategory",
  addCategory: "/addCategory",
  getCategory: "/getCategory",
  updateCategory: "/updateCategory",
  deleteCategory: "/deleteCategory",
  addSubCategory: "/addSubCategory",
  getAllSubCategory: "/getAllSubCategory",
  getSubCategory: "/getSubCategory",
  updateSubCategory: "/updateSubCategory",
  deleteSubCategory: "/deleteSubCategory",
  getProductByOgUrl: "/getProductByOgUrl",

  // Orders & Cashfree Payments
  getAllProductOrder: "/getAllProductOrder",
  getAllOrderTrack: "/getAllOrderTrack",
  trackOrder: "/trackOrder",
  addProductOrder: "/addProductOrder",
  updateOrderStatus: "/updateOrderStatus",
  updateOrder: "/updateOrder",
  deleteOrder: "/deleteOrder",
  getOrderByPhone: "/getOrderByPhone",
  generateOrder: "/generateOrder",
  getOrders: "/getOrders",
  createCashfreeOrder: "/createCashfreeOrder",
  verifyCashfreePayment: "/verifyCashfreePayment",
  getCashfreeConfig: "/getCashfreeConfig",
  resendOrderEmail: "/resendOrderEmail",

  // Addresses
  getAllAddress: "/getAllAddress",
  addAddress: "/addAddress",
  deleteAddress: "/deleteAddress",

  // Leads
  addLead: "/addLead",
  addARGLead: "/addARGLead",
  getLeads: "/getLeads",
  updateLead: "/updateLead",

  // Blog
  addBlog: "/addBlog",
  getBlogs: "/getBlogs",
  updateBlog: "/updateBlog",
  getBlog: "/getBlog",
  approveBlog: "/approveBlog",
  deleteBlog: "/deleteBlog",
  addBlogBanner: "/addBlogBanner",
  addBlogFeaturedImage: "/addBlogFeaturedImage",
  getArticles: "/getArticles",
  getArticleByOgUrl: "/getArticleByOgUrl",

  // SEO Management
  addSite: "/addSite",
  getAllSites: "/getAllSites",
  getSite: "/getSite",
  updateSite: "/updateSite",
  addMetaData: "/addMetaData",
  getMetaDataBySite: "/getMetaDataBySite",
  updateMetaData: "/updateMetaData",
  updateSeoFaqs: "/updateSeoFaqs",

  // Location Pages
  addNewPage: "/addNewPage",
  getPage: "/getPage",
  getLocationData: "/getLocationData",
  getPages: "/getPages",
  getPageByID: "/getPageByID",
  addLocation: "/addLocation",
  getLocations: "/getLocations",
  addBannerImage: "/addBannerImage",
  addGallery: "/addGallery",
  addFeaturedImage: "/addFeaturedImage",

  // Admin Specific
  adminProducts: "/admin/products",
  adminSaveProduct: "/admin/save-product",
  adminDeleteProduct: "/admin/product",

  // Sections (Developer Tooling)
  addSection: "/addSection",
  getSections: "/getSections",

  // Dashboard Stats
  getDashboardStats: "/getDashboardStats",

  // Invoice Management
  createInvoice:       "/invoice/create",
  getInvoices:         "/getInvoices",
  getInvoice:          "/getInvoice",
  updateInvoice:       "/invoice/update",
  updateInvoiceStatus: "/invoice/update-status",
  deleteInvoice:       "/invoice/delete",
  getInvoiceStats:     "/invoice/stats",
  invoicePublicView:   "/invoice/view",   // GET /invoice/view/{token}
  sendInvoiceEmail:    "/sendInvoiceEmail",

  // Track Order Alias
  trackOrder: "/track-order",
};
